import { useIntl } from 'react-intl';
import { useVisuallyHidden } from 'react-aria';

import NavLink from 'client/components/aria/NavLink/NavLink';
import Link from 'client/components/aria/Link/Link';
import SelectLocale from 'client/components/suc/Footer/SelectLocale/SelectLocale';

import useAuth from 'client/utils/auth/useAuth';

import {
    PRIVACY_PAGE,
    COOKIES_PAGE,
    CONTACT_PAGE_SIGNED_OUT,
    CONTACT_PAGE,
    COPYRIGHT_URL,
    LICENSE_URL,
    TERMS_AND_CONDITIONS_PAGE,
    HELP_PAGE,
    HELP_PAGE_SIGNED_OUT
} from 'client/utils/routes/routeConstants';

const Footer = ({ ...props }) => {
    const auth = useAuth();
    const { visuallyHiddenProps } = useVisuallyHidden();
    const intl = useIntl();

    return (
        <footer
            className="govuk-footer"
            role="contentinfo"
            data-testid="footer"
            { ...props }
        >
            <div className="govuk-width-container ">
                <div className="govuk-footer__meta">
                    <div className="govuk-footer__meta-item govuk-footer__meta-item--grow">
                        <h2 {...visuallyHiddenProps}>{intl.formatMessage({ id: 'footer.support' })}</h2>
                        <ul className="govuk-footer__inline-list">
                            { auth?.user
                                ? <>
                                    <li className="govuk-footer__inline-list-item">
                                        <NavLink
                                            data-testid="footer.link.help"
                                            to={HELP_PAGE}
                                            className="govuk-footer__link"
                                        >
                                            {intl.formatMessage({ id: 'cms.helpPage.title' })}
                                        </NavLink>
                                    </li>
                                </>
                                : <li className="govuk-footer__inline-list-item">
                                    <NavLink
                                        data-testid="footer.link.help.signed.out"
                                        to={HELP_PAGE_SIGNED_OUT}
                                        className="govuk-footer__link"
                                    >
                                        {intl.formatMessage({ id: 'cms.helpSignedOut.title' })}
                                    </NavLink>
                                </li>
                            }
                            <li className="govuk-footer__inline-list-item">
                                <NavLink
                                    data-testid="footer.link.privacy"
                                    to={PRIVACY_PAGE}
                                    className="govuk-footer__link"
                                >
                                    {intl.formatMessage({ id: 'cms.privacyPage.title' })}
                                </NavLink>
                            </li>
                            <li className="govuk-footer__inline-list-item">
                                <NavLink
                                    data-testid="footer.link.cookies"
                                    to={COOKIES_PAGE}
                                    className="govuk-footer__link"
                                >
                                    {intl.formatMessage({ id: 'cms.cookiesPage.title' })}
                                </NavLink>
                            </li>
                            { auth?.user
                                ? <>
                                    <li className="govuk-footer__inline-list-item">
                                        <NavLink
                                            data-testid="footer.link.contact"
                                            to={CONTACT_PAGE}
                                            className="govuk-footer__link"
                                        >
                                            {intl.formatMessage({ id: 'cms.contactPage.title' })}
                                        </NavLink>
                                    </li>
                                    <li className="govuk-footer__inline-list-item">
                                        <NavLink
                                            data-testid="footer.link.terms"
                                            to={TERMS_AND_CONDITIONS_PAGE}
                                            className="govuk-footer__link"
                                        >
                                            {intl.formatMessage({ id: 'termsPage.title' })}
                                        </NavLink>
                                    </li>
                                </>
                                : <li className="govuk-footer__inline-list-item">
                                    <NavLink
                                        data-testid="footer.link.contact"
                                        to={CONTACT_PAGE_SIGNED_OUT}
                                        className="govuk-footer__link"
                                    >
                                        {intl.formatMessage({ id: 'cms.contactPage.title' })}
                                    </NavLink>
                                </li>
                            }
                        </ul>
                        <ul className="govuk-footer__inline-list">
                            <SelectLocale
                                data-testid="footer.link.selectLocale"
                            />
                        </ul>
                        <svg
                            aria-hidden="true"
                            focusable="false"
                            className="govuk-footer__licence-logo"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 483.2 195.7"
                            height="17" width="41"
                        >
                            <path
                                fill="currentColor"
                                d="M421.5 142.8V.1l-50.7 32.3v161.1h112.4v-50.7zm-122.3-9.6A47.12 47.12 0 0 1 221 97.8c0-26 21.1-47.1 47.1-47.1 16.7 0 31.4 8.7 39.7 21.8l42.7-27.2A97.63 97.63 0 0 0 268.1 0c-36.5 0-68.3 20.1-85.1 49.7A98 98 0 0 0 97.8 0C43.9 0 0 43.9 0 97.8s43.9 97.8 97.8 97.8c36.5 0 68.3-20.1 85.1-49.7a97.76 97.76 0 0 0 149.6 25.4l19.4 22.2h3v-87.8h-80l24.3 27.5zM97.8 145c-26 0-47.1-21.1-47.1-47.1s21.1-47.1 47.1-47.1 47.2 21 47.2 47S123.8 145 97.8 145"
                            />
                        </svg>
                        <span
                            className="govuk-footer__licence-description"
                            data-testid="footer.license.description.1"
                        >
                            {intl.formatMessage({ id: 'footer.license.description.1' })}
                            <Link
                                className="govuk-footer__link"
                                href={LICENSE_URL}
                                rel="license"
                                data-testid="footer.link.license"
                            >
                                {intl.formatMessage({ id: 'footer.license.description.2' })}
                            </Link>
                            <span
                                data-testid="footer.license.description.3"
                            >
                                {intl.formatMessage({ id: 'footer.license.description.3' })}
                            </span>
                        </span>
                    </div>
                    <div className="govuk-footer__meta-item">
                        <Link
                            data-testid="footer.link.copyright"
                            className="govuk-footer__link govuk-footer__copyright-logo"
                            href={COPYRIGHT_URL}
                        >
                            {intl.formatMessage({ id: 'footer.copyright' })}
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
